import { memo } from "react";
import { format } from "date-fns";
import { FormattedMessage } from "react-intl";

// MUI
import theme from "assets/styles/themes";
import { Box, Typography, Popover, IconButton, Stack } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import ChipsArray from "components/Domain/ChipsArray";

// Icons
import icons from "enums/icons";

// Types
import { transactionPopoverProps } from "../../useTenure";
import { TenureChartTransactionlist } from "services/tenureChart/tenureChart.types";

interface TransactionHistoryPopupProps {
    data: TenureChartTransactionlist;
    popoverConfig: transactionPopoverProps;
    handleToggle: (popoverConfig: transactionPopoverProps) => void;
}

const TransactionHistoryPopup = memo(function TransactionHistoryPopup({
    data,
    popoverConfig,
    handleToggle,
}: TransactionHistoryPopupProps) {
    const { open, id, top, left } = popoverConfig;

    const handleClose = () => {
        handleToggle({ ...popoverConfig, open: false });
    };

    const transaction = data.transactions.find((t) => t.transaction_id === id);
    const date = transaction?.date ? format(new Date(transaction.date), "dd LLL, yyyy") : "";
    const investors = data.investors.filter((t) => t.transaction_id === id);
    const sellers = data.sellers.filter((t) => t.transaction_id === id);
    const types = data.types.filter((t) => t.transaction_id === id);

    return (
        <Popover
            open={open}
            anchorReference="anchorPosition"
            anchorPosition={open ? { top, left } : undefined}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: "9px",
                    boxShadow: "0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)",
                },
            }}
        >
            <Box sx={{ width: 420 }}>
                <Box
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <CustomIcon icon={icons.handShake} />
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}
                        >
                            <FormattedMessage id="tenureChart.transactionHistoryModal.title" />
                        </Typography>
                    </Stack>

                    <IconButton size="small" onClick={handleClose} sx={{ color: "common.white" }}>
                        <CustomIcon icon={icons.close} sx={{ fontSize: 12 }} />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ padding: 2 }}>
                    {/* Date */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
                        <CustomIcon icon={icons.users} sx={{ fontSize: 20, mt: -0.5 }} />
                        <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 700 }}>
                            <FormattedMessage id="tenureChart.transactionHistoryModal.date" />
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ ml: 1, color: theme.palette.primary.main, fontSize: 16, fontWeight: 500 }}
                        >
                            {date}
                        </Typography>
                    </Stack>

                    {/* Investors */}
                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CustomIcon icon={icons.product} sx={{ fontSize: 16 }} />
                            <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
                                <FormattedMessage id="tenureChart.transactionHistoryModal.investors" />
                            </Typography>
                        </Stack>
                        <Box sx={{ backgroundColor: theme.palette.background.grey, p: 1, borderRadius: 1.2 }}>
                            {Boolean(investors.length) && (
                                <ChipsArray
                                    chipData={investors?.map((i) => ({
                                        key: i.company_id,
                                        title: i.company_name,
                                    }))}
                                />
                            )}
                        </Box>
                    </Stack>

                    {/* Sellers */}
                    <Stack spacing={1} sx={{ mt: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CustomIcon icon={icons.product} sx={{ fontSize: 16 }} />
                            <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
                                <FormattedMessage id="tenureChart.transactionHistoryModal.sellers" />
                            </Typography>
                        </Stack>
                        <Box sx={{ backgroundColor: theme.palette.background.grey, p: 1, borderRadius: 1.2 }}>
                            {Boolean(sellers.length) && (
                                <ChipsArray
                                    chipData={sellers?.map((s) => ({ key: s.company_id, title: s.company_name }))}
                                />
                            )}
                        </Box>
                    </Stack>

                    {/* Transaction Types */}
                    <Stack spacing={1} sx={{ mt: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CustomIcon icon={icons.deals} style={{ fontSize: "16px" }} />
                            <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
                                <FormattedMessage id="tenureChart.transactionHistoryModal.transactionTypes" />
                            </Typography>
                        </Stack>
                        <Box sx={{ backgroundColor: theme.palette.background.grey, p: 1, borderRadius: 1.2 }}>
                            {Boolean(types.length) && (
                                <ChipsArray chipData={types.map((t) => ({ key: t.transaction_id, title: t.name }))} />
                            )}
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </Popover>
    );
});

export default TransactionHistoryPopup;
